<template>
  <div class="holiday-manage-page">
    <LoadingOverlay :show="loading || saving" :text="saving ? '저장 중...' : '불러오는 중...'" />

    <header class="console-page-head">
      <div class="console-page-copy">
        <p class="console-kicker">설정</p>
        <h2 class="console-title">공휴일관리</h2>
      </div>
      <div class="console-head-side">
        <v-btn
          variant="flat"
          class="btn-refresh"
          :disabled="loading || missingFixedCount === 0"
          @click="ensureYear"
        >
          <v-icon size="16" class="mr-1">mdi-sync</v-icon>
          빠진 고정 {{ missingFixedCount }}건 반영
        </v-btn>
      </div>
    </header>

    <div
      v-if="missingFixedCount > 0"
      class="hm-banner hm-banner--warn"
    >
      <div class="hm-banner-text">
        <strong>{{ viewYear }}년</strong> 달력에 고정 공휴일
        <strong>{{ missingFixedCount }}건</strong>이 빠져 있습니다.
        <span class="hm-banner-sub">삭제했거나 아직 반영하지 않은 항목입니다.</span>
      </div>
      <v-btn variant="flat" class="btn-save" size="small" :disabled="loading" @click="ensureYear">
        지금 반영
      </v-btn>
    </div>

    <div class="hm-body">
      <aside class="hm-side">
        <!-- 선택 연도 상태 요약 -->
        <div class="hm-status">
          <div class="hm-status-year">{{ viewYear }}</div>
          <div class="hm-status-grid">
            <div class="hm-stat ok">
              <span class="hm-stat-n">{{ seededFixedCount }}</span>
              <span class="hm-stat-l">반영됨</span>
            </div>
            <div class="hm-stat" :class="missingFixedCount ? 'warn' : 'muted'">
              <span class="hm-stat-n">{{ missingFixedCount }}</span>
              <span class="hm-stat-l">빠짐</span>
            </div>
            <div class="hm-stat muted">
              <span class="hm-stat-n">{{ pendingFixedCount }}</span>
              <span class="hm-stat-l">아직 적용 전</span>
            </div>
            <div class="hm-stat custom">
              <span class="hm-stat-n">{{ customYearHolidays.length }}</span>
              <span class="hm-stat-l">수동</span>
            </div>
          </div>
        </div>

        <!-- 고정 목록: 상태 1개만 -->
        <div class="hm-panel">
          <div class="hm-panel-head">
            <span>고정 (양력)</span>
            <span class="hm-panel-count">{{ fixedList.length }}</span>
          </div>
          <ul class="hm-fixed-list">
            <li
              v-for="f in fixedList"
              :key="f.idx"
              class="hm-fixed-item"
              :class="fixedRowState(f)"
              @click="jumpToFixed(f)"
            >
              <span class="hm-fixed-dot" aria-hidden="true" />
              <div class="hm-fixed-body">
                <div class="hm-fixed-top">
                  <span class="hm-fixed-md">{{ f.mmdd }}</span>
                  <span class="hm-fixed-name">{{ f.name }}</span>
                </div>
                <div class="hm-fixed-sub">
                  <template v-if="fixedRowState(f) === 'pending'">
                    {{ f.fromYear }}년부터 적용 · 지금은 해당 없음
                  </template>
                  <template v-else-if="fixedRowState(f) === 'missing'">
                    {{ viewYear }}년 달력에 없음 · 상단에서 반영
                  </template>
                  <template v-else>
                    <span v-if="f.fromYear">{{ f.fromYear }}~ · </span>{{ viewYear }}년 반영됨
                  </template>
                </div>
              </div>
            </li>
          </ul>
          <p class="hm-foot-note">
            제헌절처럼 시작 연도가 있으면 그 해부터만 달력에 올라갑니다.
            고정 항목을 지워도 목록은 남고, 「빠진 고정 반영」으로 복구합니다.
          </p>
        </div>

        <!-- 수동: 있는 달만 -->
        <div class="hm-panel hm-panel--grow">
          <div class="hm-panel-head">
            <span>수동 (설·추석·대체 등)</span>
            <span class="hm-panel-count">{{ customYearHolidays.length }}</span>
          </div>
          <p v-if="!customYearHolidays.length" class="hm-empty">
            없음 · 달력 날짜를 눌러 추가
          </p>
          <ul v-else class="hm-custom-list">
            <li
              v-for="h in customYearHolidaysSorted"
              :key="h.idx"
              class="hm-custom-item"
              @click="openDay(h.date)"
            >
              <span class="hm-custom-date">{{ formatShortDate(h.date) }}</span>
              <span class="hm-custom-name">{{ h.name }}</span>
            </li>
          </ul>
        </div>
      </aside>

      <main class="hm-main">
        <div class="hm-cal-toolbar">
          <div class="hm-ym-selects">
            <v-select
              :model-value="viewYear"
              :items="yearSelectItems"
              item-title="title"
              item-value="value"
              variant="outlined"
              density="compact"
              hide-details
              class="hm-v-select hm-v-select--year"
              @update:model-value="onYearChange"
            />
            <v-select
              :model-value="viewMonth"
              :items="monthSelectItems"
              item-title="title"
              item-value="value"
              variant="outlined"
              density="compact"
              hide-details
              class="hm-v-select hm-v-select--month"
              @update:model-value="onMonthChange"
            />
          </div>
          <div class="hm-cal-legend">
            <span class="lg fixed"><i />고정</span>
            <span class="lg custom"><i />수동</span>
            <span class="lg today"><i />오늘</span>
          </div>
          <v-spacer />
          <v-btn variant="flat" class="btn-reset" @click="goToday">오늘</v-btn>
        </div>

        <div class="hm-weekdays">
          <span v-for="w in weekLabels" :key="w" :class="{ sun: w === '일', sat: w === '토' }">{{ w }}</span>
        </div>

        <div class="hm-grid">
          <button
            v-for="cell in calendarCells"
            :key="cell.key"
            type="button"
            class="hm-cell"
            :class="{
              out: !cell.inMonth,
              sun: cell.dow === 0,
              sat: cell.dow === 6,
              today: cell.isToday,
              holiday: !!cell.holiday,
              fixed: cell.holiday?.source === 'fixed',
              custom: cell.holiday?.source === 'custom',
            }"
            :disabled="!cell.inMonth"
            @click="cell.inMonth && openDay(cell.date)"
          >
            <span class="hm-cell-num">{{ cell.day }}</span>
            <span v-if="cell.holiday" class="hm-cell-name">{{ cell.holiday.name }}</span>
          </button>
        </div>
      </main>
    </div>

    <v-dialog v-model="dialogOpen" max-width="440" persistent>
      <v-card class="hm-dialog">
        <v-card-title class="modal-header">
          <span>{{ dialogDateLabel }}</span>
          <button
            type="button"
            class="hm-dialog__close"
            :disabled="saving"
            aria-label="닫기"
            @click="closeDialog"
          >
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </v-card-title>
        <v-card-text>
          <div v-if="editing?.source === 'fixed'" class="hm-dialog-callout">
            고정 공휴일 · <strong>{{ viewYear }}년만</strong> 삭제됩니다. 목록(템플릿)은 유지되고,
            다시 넣으려면 「빠진 고정 반영」을 누르세요.
          </div>
          <div v-else class="hm-dialog-callout hm-dialog-callout--muted">
            설·추석·대체공휴일 등 해마다 다른 날은 여기에 직접 등록합니다.
          </div>
          <v-text-field
            v-model="formName"
            label="공휴일명"
            variant="outlined"
            density="compact"
            hide-details="auto"
            maxlength="50"
            autofocus
            class="hm-dialog-field"
            @keyup.enter="saveHoliday"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn
            v-if="editing"
            variant="flat"
            class="btn-delete"
            :disabled="saving"
            @click="deleteHoliday"
          >
            삭제
          </v-btn>
          <v-spacer />
          <v-btn variant="text" class="btn-cancel" :disabled="saving" @click="closeDialog">취소</v-btn>
          <v-btn variant="flat" class="btn-save" :disabled="saving || !formName.trim()" @click="saveHoliday">
            저장
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';
import { holidayManageApi } from '@/services/api';

interface HolidayItem {
  idx: number;
  date: string;
  name: string;
  source: 'fixed' | 'custom';
}

interface FixedItem {
  idx: number;
  month: number;
  day: number;
  name: string;
  mmdd: string;
  fromYear: number | null;
}

type FixedRowState = 'seeded' | 'missing' | 'pending';

const weekLabels = ['일', '월', '화', '수', '목', '금', '토'];

const now = new Date();
const viewYear = ref(now.getFullYear());
const viewMonth = ref(now.getMonth() + 1);
const loading = ref(false);
const saving = ref(false);
const yearHolidays = ref<HolidayItem[]>([]);
const fixedList = ref<FixedItem[]>([]);

const dialogOpen = ref(false);
const dialogDate = ref('');
const formName = ref('');
const editing = ref<HolidayItem | null>(null);

const yearSelectItems = computed(() => {
  const cur = new Date().getFullYear();
  const years: number[] = [];
  for (let y = cur + 5; y >= cur - 12; y -= 1) years.push(y);
  if (!years.includes(viewYear.value)) {
    years.push(viewYear.value);
    years.sort((a, b) => b - a);
  }
  return years.map((y) => ({ value: y, title: `${y}년` }));
});

const monthSelectItems = computed(() =>
  Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    title: `${i + 1}월`,
  }))
);

const pad2 = (n: number) => String(n).padStart(2, '0');

const holidayMap = computed(() => {
  const map: Record<string, HolidayItem> = {};
  for (const h of yearHolidays.value) {
    map[h.date] = h;
  }
  return map;
});

const customYearHolidays = computed(() =>
  yearHolidays.value.filter((h) => h.source !== 'fixed')
);

const customYearHolidaysSorted = computed(() =>
  [...customYearHolidays.value].sort((a, b) => a.date.localeCompare(b.date))
);

const fixedDateKey = (f: FixedItem, year: number) =>
  `${year}-${pad2(f.month)}-${pad2(f.day)}`;

const isFixedActiveForViewYear = (f: FixedItem) =>
  !(f.fromYear != null && viewYear.value < f.fromYear);

const isFixedSeededOnCalendar = (f: FixedItem) => {
  if (!isFixedActiveForViewYear(f)) return false;
  const hit = holidayMap.value[fixedDateKey(f, viewYear.value)];
  return !!hit && hit.source === 'fixed';
};

const fixedRowState = (f: FixedItem): FixedRowState => {
  if (!isFixedActiveForViewYear(f)) return 'pending';
  return isFixedSeededOnCalendar(f) ? 'seeded' : 'missing';
};

const seededFixedCount = computed(
  () => fixedList.value.filter((f) => fixedRowState(f) === 'seeded').length
);
const missingFixedCount = computed(
  () => fixedList.value.filter((f) => fixedRowState(f) === 'missing').length
);
const pendingFixedCount = computed(
  () => fixedList.value.filter((f) => fixedRowState(f) === 'pending').length
);

const todayKey = computed(() => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
});

const calendarCells = computed(() => {
  const y = viewYear.value;
  const m = viewMonth.value;
  const first = new Date(y, m - 1, 1);
  const startDow = first.getDay();
  const daysInMonth = new Date(y, m, 0).getDate();
  const prevDays = new Date(y, m - 1, 0).getDate();

  const cells: Array<{
    key: string;
    date: string;
    day: number;
    dow: number;
    inMonth: boolean;
    isToday: boolean;
    holiday?: HolidayItem;
  }> = [];

  for (let i = 0; i < 42; i++) {
    let day: number;
    let inMonth = true;
    let cellY = y;
    let cellM = m;

    if (i < startDow) {
      day = prevDays - startDow + i + 1;
      inMonth = false;
      cellM = m === 1 ? 12 : m - 1;
      cellY = m === 1 ? y - 1 : y;
    } else if (i >= startDow + daysInMonth) {
      day = i - startDow - daysInMonth + 1;
      inMonth = false;
      cellM = m === 12 ? 1 : m + 1;
      cellY = m === 12 ? y + 1 : y;
    } else {
      day = i - startDow + 1;
    }

    const date = `${cellY}-${pad2(cellM)}-${pad2(day)}`;
    cells.push({
      key: `${date}-${i}`,
      date,
      day,
      dow: i % 7,
      inMonth,
      isToday: date === todayKey.value,
      holiday: inMonth ? holidayMap.value[date] : undefined,
    });
  }
  return cells;
});

const dialogDateLabel = computed(() => {
  if (!dialogDate.value) return '공휴일';
  const [y, mo, d] = dialogDate.value.split('-').map(Number);
  const dow = new Date(y, mo - 1, d).getDay();
  return `${y}년 ${mo}월 ${d}일 (${weekLabels[dow]})`;
});

const formatShortDate = (date: string) => {
  const m = Number(date.slice(5, 7));
  const d = Number(date.slice(8, 10));
  return `${m}/${d}`;
};

const mapHolidayItems = (items: any[]): HolidayItem[] =>
  (items || []).map((h: any) => ({
    idx: h.idx,
    date: String(h.date).substring(0, 10),
    name: h.name || '',
    source: h.source === 'fixed' ? 'fixed' : 'custom',
  }));

const loadFixed = async () => {
  try {
    const res = await holidayManageApi.getFixed();
    if (res.data?.success) {
      fixedList.value = (res.data.data?.items || []).map((f: any) => ({
        idx: f.idx,
        month: Number(f.month),
        day: Number(f.day),
        name: String(f.name || ''),
        mmdd: String(f.mmdd || `${pad2(f.month)}-${pad2(f.day)}`),
        fromYear: f.fromYear != null ? Number(f.fromYear) : null,
      }));
    }
  } catch (e: any) {
    console.error(e);
  }
};

const loadYear = async () => {
  loading.value = true;
  try {
    const res = await holidayManageApi.listByYear(viewYear.value);
    if (!res.data?.success) {
      throw new Error(res.data?.message || '공휴일 조회 실패');
    }
    yearHolidays.value = mapHolidayItems(res.data.data?.items || []);
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`공휴일 조회 실패: ${message}`);
    console.error('공휴일 조회 오류:', error);
  } finally {
    loading.value = false;
  }
};

const ensureYear = async () => {
  loading.value = true;
  try {
    const res = await holidayManageApi.ensureYear(viewYear.value);
    if (!res.data?.success) {
      throw new Error(res.data?.message || '반영 실패');
    }
    yearHolidays.value = mapHolidayItems(res.data.data?.items || []);
    const inserted = res.data.data?.seed?.inserted ?? 0;
    alert(
      inserted > 0
        ? `${viewYear.value}년 고정 공휴일 ${inserted}건을 반영했습니다.`
        : `${viewYear.value}년 고정 공휴일은 모두 반영되어 있습니다.`
    );
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`고정 공휴일 반영 실패: ${message}`);
  } finally {
    loading.value = false;
  }
};

const onYearChange = (y: number) => {
  if (Number.isFinite(y)) viewYear.value = y;
};

const onMonthChange = (m: number) => {
  if (Number.isFinite(m) && m >= 1 && m <= 12) viewMonth.value = m;
};

const jumpToFixed = (f: FixedItem) => {
  if (!isFixedActiveForViewYear(f)) return;
  viewMonth.value = f.month;
  const key = fixedDateKey(f, viewYear.value);
  if (holidayMap.value[key]) openDay(key);
};

const goToday = () => {
  const d = new Date();
  viewYear.value = d.getFullYear();
  viewMonth.value = d.getMonth() + 1;
};

const openDay = (date: string) => {
  dialogDate.value = date;
  editing.value = holidayMap.value[date] || null;
  formName.value = editing.value?.name || '';
  dialogOpen.value = true;
};

const closeDialog = () => {
  dialogOpen.value = false;
  formName.value = '';
  editing.value = null;
  dialogDate.value = '';
};

const saveHoliday = async () => {
  const name = formName.value.trim();
  if (!name || !dialogDate.value) return;
  saving.value = true;
  try {
    const res = await holidayManageApi.upsert({ date: dialogDate.value, name });
    if (!res.data?.success) {
      throw new Error(res.data?.message || '저장 실패');
    }
    await loadYear();
    closeDialog();
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`공휴일 저장 실패: ${message}`);
  } finally {
    saving.value = false;
  }
};

const deleteHoliday = async () => {
  if (!editing.value) return;
  const isFixed = editing.value.source === 'fixed';
  const msg = isFixed
    ? `「${editing.value.name}」을(를) ${viewYear.value}년에서만 삭제할까요?\n목록에는 남고, 나중에 「빠진 고정 반영」으로 복구할 수 있습니다.`
    : `「${editing.value.name}」을(를) 삭제할까요?`;
  if (!confirm(msg)) return;
  saving.value = true;
  try {
    const res = await holidayManageApi.remove(editing.value.idx);
    if (!res.data?.success) {
      throw new Error(res.data?.message || '삭제 실패');
    }
    await loadYear();
    closeDialog();
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`공휴일 삭제 실패: ${message}`);
  } finally {
    saving.value = false;
  }
};

watch(viewYear, () => {
  loadYear();
});

onMounted(async () => {
  await loadFixed();
  await loadYear();
});
</script>
